import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Clapperboard, Eye, Heart, PlayCircle, Plus, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StudioListSkeleton } from '../components/ExploreSkeletons';
import StudioTutorialModal, { markStudioTutorialSeen, studioTutorialSeen } from '../components/StudioTutorialModal';
import { isPlatformCreator, studioCall, type ExploreCategory } from '../lib/exploreTemplates';
import '../explore.css';

type StudioTemplate = {
  id: string;
  title: string;
  status: string;
  duration_seconds: number;
  aspect_ratio: string;
  updated_at: string;
  preview_video_url: string | null;
  poster_url: string | null;
  view_count: number;
  like_count: number;
  save_count: number;
  use_count: number;
};

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const number = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
const when = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' });

export default function CreatorStudio() {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [templates, setTemplates] = useState<StudioTemplate[]>([]);
  const [categories, setCategories] = useState<ExploreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [savingTheme, setSavingTheme] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const load = useCallback(async () => {
    const data = await studioCall<{ templates: StudioTemplate[]; categories: ExploreCategory[] }>({ action: 'list' });
    setTemplates(data.templates || []);
    setCategories(data.categories || []);
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    isPlatformCreator(user.id).then(async (ok) => {
      if (cancel) return;
      setAllowed(ok);
      if (!ok) return;
      try {
        await load();
      } catch (err) {
        if (!cancel) setError(err instanceof Error ? err.message : 'Não foi possível abrir o estúdio.');
      } finally {
        if (!cancel) setLoading(false);
      }
    });
    return () => { cancel = true; };
  }, [user, load]);

  useEffect(() => {
    if (allowed && !studioTutorialSeen()) setTutorialOpen(true);
  }, [allowed]);

  const closeTutorial = useCallback(() => {
    markStudioTutorialSeen();
    setTutorialOpen(false);
  }, []);

  if (allowed === null) {
    return (
      <div className="explore-page">
        <header className="explore-hero"><h1>Estúdio</h1></header>
        <StudioListSkeleton />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="explore-page">
        <header className="explore-hero"><h1>Estúdio</h1></header>
        <div className="explore-state">
          <Clapperboard size={28} aria-hidden />
          <p className="explore-state-title">Sua conta ainda não publica vídeos</p>
          <p className="explore-note">Peça acesso de criador a um administrador para montar modelos para a equipe.</p>
        </div>
      </div>
    );
  }

  const addCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = label.trim();
    const slug = slugify(clean);
    if (!clean || !slug) return;
    setError(null);
    setSavingTheme(true);
    try {
      await studioCall({ action: 'save_category', label: clean, slug });
      setLabel('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível gravar o tema. Tente outro nome.');
    } finally {
      setSavingTheme(false);
    }
  };

  const published = templates.filter((t) => t.status === 'published').length;

  return (
    <div className="explore-page">
      <header className="studio-head">
        <div>
          <h1>Estúdio</h1>
          <p className="explore-lead">
            {loading
              ? 'Carregando seus modelos…'
              : templates.length === 0
                ? 'Crie o primeiro modelo e publique na home da equipe.'
                : `${templates.length} ${templates.length === 1 ? 'modelo' : 'modelos'} · ${published} ${published === 1 ? 'publicado' : 'publicados'}`}
          </p>
        </div>
        <button type="button" className="explore-btn" onClick={() => setTutorialOpen(true)}>
          <PlayCircle size={16} aria-hidden /> Tutorial
        </button>
      </header>

      {error && <p className="explore-error" role="alert">{error}</p>}

      {loading ? (
        <StudioListSkeleton />
      ) : (
        <div className="studio-grid">
          <Link to="/estudio/novo" className="studio-tile-new">
            <span className="studio-tile-plus" aria-hidden><Plus size={22} /></span>
            <strong>Novo modelo</strong>
            <span>Prompt, parâmetros e prévia</span>
          </Link>
          {templates.map((item) => (
            <Link key={item.id} to={`/estudio/${item.id}`} className="studio-tile">
              <div className="studio-tile-media">
                {item.poster_url && <img src={item.poster_url} alt="" width={400} height={250} loading="lazy" decoding="async" />}
                {item.preview_video_url ? (
                  <video src={`${item.preview_video_url}#t=0.1`} muted playsInline preload="metadata" aria-hidden tabIndex={-1} />
                ) : !item.poster_url ? (
                  <span className="studio-tile-empty"><Clapperboard size={22} aria-hidden /> Sem prévia</span>
                ) : null}
                <span className={`studio-pill ${item.status === 'published' ? 'is-live' : ''}`}>
                  {item.status === 'published' ? 'Publicado' : 'Rascunho'}
                </span>
                <span className="explore-duration">{item.duration_seconds}&nbsp;s</span>
              </div>
              <div className="studio-tile-body">
                <strong>{item.title || 'Sem título'}</strong>
                <span className="studio-stats">
                  <span title="Visualizações"><Eye size={13} aria-hidden /><span className="ex-sr">Visualizações</span> {number.format(item.view_count)}</span>
                  <span title="Curtidas"><Heart size={13} aria-hidden /><span className="ex-sr">Curtidas</span> {number.format(item.like_count)}</span>
                  <span title="Salvos"><Bookmark size={13} aria-hidden /><span className="ex-sr">Salvos</span> {number.format(item.save_count)}</span>
                  <span title="Vídeos criados a partir deste modelo"><Clapperboard size={13} aria-hidden /><span className="ex-sr">Criados</span> {number.format(item.use_count)}</span>
                </span>
                <span className="explore-note">Editado em {when.format(new Date(item.updated_at))}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <section className="studio-themes-block" aria-labelledby="studio-themes-title">
        <h2 id="studio-themes-title" className="studio-section"><Tag size={16} aria-hidden /> Temas</h2>
        <p className="explore-note">Os temas viram os filtros da home. Use poucos e claros.</p>
        <ul className="studio-themes">
          {categories.map((cat) => <li key={cat.id}>{cat.label}</li>)}
          {categories.length === 0 && !loading && <li className="is-empty">Nenhum tema ainda</li>}
        </ul>
        <form className="studio-cat" onSubmit={(e) => void addCategory(e)}>
          <label className="ex-sr" htmlFor="studio-theme-label">Nome do novo tema</label>
          <input
            id="studio-theme-label"
            name="theme"
            type="text"
            autoComplete="off"
            maxLength={40}
            value={label}
            placeholder="Ex.: Lançamentos…"
            onChange={(e) => setLabel(e.target.value)}
          />
          <button type="submit" className="explore-btn" disabled={savingTheme || !slugify(label)}>
            {savingTheme ? 'Salvando…' : 'Adicionar tema'}
          </button>
        </form>
      </section>

      <StudioTutorialModal open={tutorialOpen} onClose={closeTutorial} />
    </div>
  );
}
